/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useNavigate } from "react-router-dom";
import { Button, ModalConfirm } from "@nordcloud/gnui";
import { ACTION, BreadcrumbsBox, PopoverControls } from "~/components";
import { useDisclosure } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { generateActionConfirmText } from "~/tools";
import { useCloudAccountBatchDelete } from "../hooks";
import { CloudAccount, ProviderTab } from "../types";
import {
  canBeOnboarded,
  downloadDeployment,
  getAccountDisplayName,
  getAccountFilters,
  hasNoData,
  isVMWare,
  mapProviderTab,
} from "../utils";

type Props = {
  tags: { color: string; text: string }[];
  account: CloudAccount;
  openCredentialsUpdateSteps: () => void;
  showUpdateCredentials: boolean;
  openDocumentationSidebar: () => void;
  openOnboardSidebar: () => void;
};

export function Header({
  tags,
  account,
  openCredentialsUpdateSteps,
  showUpdateCredentials,
  openOnboardSidebar,
  openDocumentationSidebar,
}: Props) {
  const navigate = useNavigate();

  const accountName = getAccountDisplayName(account);
  const { provider, providerType, cloudStatus, nid } = account;
  const breadcrumbLabels = [
    { uri: ROUTES.index, label: "Home" },
    { uri: ROUTES.cloudAccounts.index, label: "Cloud Accounts" },
    { uri: ROUTES.cloudAccounts.details, label: accountName },
  ];

  const providerTab = mapProviderTab(provider, providerType) || ProviderTab.AWS;

  const [handleDeleteAccounts] = useCloudAccountBatchDelete({
    responseParameters: {
      limit: 20,
      filter: {
        ...getAccountFilters(providerTab),
      },
    },
  });

  const {
    open: showDeleteModal,
    close: closeDeleteModal,
    isOpen: isDeleteModalOpen,
  } = useDisclosure();

  const onDelete = async () => {
    await handleDeleteAccounts([nid]);

    closeDeleteModal();

    navigate(`${ROUTES.cloudAccounts.index}?provider=${providerTab}`);
  };

  const deleteAction = {
    label: "Delete Account",
    action: ACTION.DELETE,
    onClick: showDeleteModal,
  };

  const popoverActions = [deleteAction];

  if (showUpdateCredentials) {
    const updateCredentials = {
      label: "Update Credentials",
      action: ACTION.CUSTOM_TRIGGER,
      customIcon: "settings",
      onClick: openCredentialsUpdateSteps,
    };
    popoverActions.unshift(updateCredentials);
  }

  if (canBeOnboarded(providerType, cloudStatus)) {
    const onboard = {
      label: "Onboard Account",
      action: ACTION.EDIT,
      onClick: openOnboardSidebar,
    };
    popoverActions.unshift(onboard);
  }

  if (hasNoData(providerType, cloudStatus)) {
    const documentation = {
      label: "Documentation",
      action: ACTION.CUSTOM_TRIGGER,
      customIcon: "info",
      onClick: openDocumentationSidebar,
    };
    popoverActions.unshift(documentation);

    const buttonText = isVMWare(providerType)
      ? "Download Config File"
      : "Download Deployment YAML";

    const download = {
      label: buttonText,
      action: ACTION.CUSTOM_TRIGGER,
      customIcon: "download",
      onClick: () => {
        downloadDeployment(account);
      },
    };
    popoverActions.unshift(download);
  }

  return (
    <>
      <BreadcrumbsBox
        title={accountName}
        tags={tags}
        customBreadcrumbs={breadcrumbLabels}
      >
        <PopoverControls
          trigger={<Button severity="low" icon="menu" />}
          items={popoverActions}
        />
      </BreadcrumbsBox>
      <ModalConfirm
        isOpen={isDeleteModalOpen}
        confirm={onDelete}
        actionLabel="Delete"
        contentLabel="Delete Account"
        onClose={closeDeleteModal}
      >
        {generateActionConfirmText(accountName)("delete")()}
      </ModalConfirm>
    </>
  );
}
